import React, { useState } from 'react';
import { CalendarDays, Home, MapPin, CheckCircle } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import axios from 'axios';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export interface VisitFollowupMetadata {
  type: 'visit_followup';
  contactRequestId: string;
  clientName?: string;
  propertyTitle?: string;
  propertyAddress?: string;
  visitDate?: string;
  actions?: Array<{ label: string; value: string }>;
}

interface VisitFollowupCardProps {
  content: string;
  metadata: VisitFollowupMetadata;
  timestamp: string;
  isOwn: boolean;
  senderName?: string;
  onDecisionSubmitted?: () => void;
}

export function VisitFollowupCard({
  content,
  metadata,
  timestamp,
  isOwn,
  senderName,
  onDecisionSubmitted,
}: VisitFollowupCardProps) {
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [submittedDecision, setSubmittedDecision] = useState<string | null>(null);
  const { toast } = useToast();

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatVisitDate = (dateString?: string) => {
    if (!dateString) return null;
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const handleDecision = async (decision: string) => {
    if (submitting || submittedDecision) return;

    setSubmitting(decision);
    try {
      await axios.post(
        `${API}/client/contact-requests/${metadata.contactRequestId}/visit-decision`,
        null,
        {
          params: { decision },
        }
      );

      setSubmittedDecision(decision);
      toast({
        title: 'Décision enregistrée',
        description: 'Votre décision a été transmise avec succès',
      });

      if (onDecisionSubmitted) {
        onDecisionSubmitted();
      }
    } catch (error: any) {
      console.error('Error submitting visit decision:', error);
      toast({
        title: 'Erreur',
        description: error.response?.data?.message || 'Impossible d\'enregistrer votre décision',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(null);
    }
  };

  const visitDateText = formatVisitDate(metadata.visitDate);
  const decisionLabels: Record<string, string> = {
    WANT: 'Je veux le bien',
    THINKING: 'Je réfléchis encore',
    NOT_INTERESTED: 'Je ne veux pas le bien',
  };

  // If decision already submitted, show confirmation
  if (submittedDecision && !isOwn) {
    return (
      <div className="flex w-full mb-4 justify-start">
        <div className="max-w-[85%] rounded-2xl shadow-sm overflow-hidden bg-green-50 border border-green-200 text-gray-900 rounded-bl-md">
          <div className="px-4 py-3">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <p className="text-sm font-medium text-green-900">
                {decisionLabels[submittedDecision] || 'Décision enregistrée'}
              </p>
            </div>
          </div>
          <div className="px-4 pb-2">
            <span className="text-[10px] text-gray-400">
              {formatTime(timestamp)}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex w-full mb-4',
        isOwn ? 'justify-end' : 'justify-start'
      )}
    >
      <div
        className={cn(
          'max-w-[85%] rounded-2xl shadow-sm overflow-hidden',
          isOwn
            ? 'bg-[#FF9F68] text-white rounded-br-md'
            : 'bg-white border border-gray-200 text-gray-900 rounded-bl-md'
        )}
      >
        {/* Header with sender name */}
        {!isOwn && senderName && (
          <div className="px-4 pt-3">
            <p className="text-xs font-medium text-[#FF9F68]">
              {senderName}
            </p>
          </div>
        )}

        {/* Message content */}
        <div className="px-4 py-3">
          <p className={cn(
            "text-sm whitespace-pre-wrap leading-relaxed",
            isOwn ? "text-white" : "text-gray-700"
          )}>
            {content}
          </p>
        </div>

        {/* Property Info Card */}
        <div className={cn(
          "mx-3 mb-3 p-4 rounded-xl",
          isOwn
            ? "bg-white/10 border border-white/20"
            : "bg-gray-50 border border-gray-200"
        )}>
          <div className="flex items-start gap-3">
            <div className={cn(
              "p-2 rounded-lg",
              isOwn ? "bg-white/20" : "bg-[#FF9F68]/10"
            )}>
              <CalendarDays className={cn(
                "h-5 w-5",
                isOwn ? "text-white" : "text-[#FF9F68]"
              )} />
            </div>
            <div className="flex-1 min-w-0">
              {metadata.propertyTitle && (
                <div className="flex items-center gap-1.5 mb-1">
                  <Home className={cn(
                    "h-3.5 w-3.5 flex-shrink-0",
                    isOwn ? "text-blue-200" : "text-gray-500"
                  )} />
                  <span className={cn(
                    "text-xs font-medium truncate",
                    isOwn ? "text-blue-100" : "text-gray-700"
                  )}>
                    {metadata.propertyTitle}
                  </span>
                </div>
              )}
              {metadata.propertyAddress && (
                <div className="flex items-center gap-1.5 mb-1">
                  <MapPin className={cn(
                    "h-3.5 w-3.5 flex-shrink-0",
                    isOwn ? "text-blue-200" : "text-gray-500"
                  )} />
                  <span className={cn(
                    "text-xs truncate",
                    isOwn ? "text-blue-100" : "text-gray-600"
                  )}>
                    {metadata.propertyAddress}
                  </span>
                </div>
              )}
              {visitDateText && (
                <p className={cn(
                  "text-xs mt-1",
                  isOwn ? "text-blue-100" : "text-gray-700"
                )}>
                  Visite effectuée le {visitDateText}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons - Only show for client (not own message) */}
        {!isOwn && metadata.actions && metadata.actions.length > 0 && (
          <div className="px-4 pb-3 space-y-2">
            {metadata.actions.map((action) => (
              <Button
                key={action.value}
                onClick={() => handleDecision(action.value)}
                disabled={submitting !== null || submittedDecision !== null}
                className={cn(
                  "w-full justify-start text-sm",
                  action.value === 'WANT'
                    ? "bg-green-600 hover:bg-green-700 text-white"
                    : action.value === 'NOT_INTERESTED'
                    ? "bg-red-600 hover:bg-red-700 text-white"
                    : "bg-gray-600 hover:bg-gray-700 text-white"
                )}
                variant="default"
              >
                {submitting === action.value ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></span>
                    Envoi...
                  </span>
                ) : (
                  action.label
                )}
              </Button>
            ))}
          </div>
        )}

        {/* Timestamp */}
        <div className={cn(
          "px-4 pb-2 flex items-center",
          isOwn ? "justify-end" : "justify-start"
        )}>
          <span className={cn(
            "text-[10px]",
            isOwn ? "text-blue-100" : "text-gray-400"
          )}>
            {formatTime(timestamp)}
          </span>
        </div>
      </div>
    </div>
  );
}

export default VisitFollowupCard;

